import { useEffect, useRef, useState, useSyncExternalStore, type ReactNode } from 'react';
import type { Phase } from '@nandanam/domain';
import { Icon } from './icons';
import { PHASE_LABEL } from '../app/selectors';
import { signIn } from '../onedrive/auth';
import { useStore } from '../app/store';
import { dismiss, dismissed, install, installKind, onInstallChange } from '../app/install';

export function StatusPill({ phase, label }: { phase: Phase | string; label?: string }) {
  return <span className={`pill st-${phase}`}>{label ?? PHASE_LABEL[phase as Phase] ?? phase}</span>;
}

export function Tag({ tone = 'neutral', children }: { tone?: 'neutral' | 'good' | 'bad' | 'warn' | 'info' | 'hold'; children: ReactNode }) {
  return <span className={`pill st-${tone}`}>{children}</span>;
}

export function Avatar({ name, size = 34, bg = '#e1ebe9', fg = '#2F4F4F' }: { name: string; size?: number; bg?: string; fg?: string }) {
  const ini = name.replace(/&/g, ' ').split(/\s+/).filter(Boolean).slice(0, 2).map(w => w[0]!.toUpperCase()).join('');
  return <span style={{ width: size, height: size, borderRadius: '50%', background: bg, color: fg, display: 'grid', placeItems: 'center', fontSize: size * 0.36, fontWeight: 700, flex: 'none' }}>{ini}</span>;
}

export function Tile({ icon, bg, fg, size = 34 }: { icon: string; bg: string; fg: string; size?: number }) {
  return <span className="tile" style={{ width: size, height: size, borderRadius: size > 36 ? 12 : 11, background: bg, color: fg }}><Icon name={icon} size={size > 36 ? 18 : 17} /></span>;
}

export function PageHead({ icon, title, sub, children }: { icon: string; title: ReactNode; sub?: ReactNode; children?: ReactNode }) {
  return (
    <header className="page-head">
      <span className="head-tile"><Icon name={icon} size={22} /></span>
      <div style={{ minWidth: 0 }}>
        <h1 className="page-title">{title}</h1>
        {sub && <div className="page-sub">{sub}</div>}
      </div>
      {children && <div className="head-actions">{children}</div>}
    </header>
  );
}

/** Centred dialog; on phones the same component renders as a bottom sheet (see mobile.css). */
export function Modal({ title, kicker, sub, size, onClose, children, footer }: { title: ReactNode; kicker?: string; sub?: ReactNode; size?: 'lg' | 'xl'; onClose: () => void; children: ReactNode; footer?: ReactNode }) {
  const ref = useRef<HTMLDivElement>(null);
  useEffect(() => {
    const k = (e: KeyboardEvent) => { if (e.key === 'Escape') onClose(); };
    addEventListener('keydown', k);
    ref.current?.querySelector<HTMLElement>('input, select, textarea, button.primary')?.focus();
    return () => removeEventListener('keydown', k);
  }, [onClose]);
  return (
    <div className="backdrop" onMouseDown={e => { if (e.target === e.currentTarget) onClose(); }}>
      <div className={`modal ${size ?? ''}`} role="dialog" aria-modal="true" ref={ref}>
        <div className="sheet-grab" />
        <div className="modal-head">
          <div style={{ flex: 1, minWidth: 0 }}>
            {kicker && <div className="kicker">{kicker}</div>}
            <h2>{title}</h2>
            {sub && <div className="muted" style={{ fontSize: 13, marginTop: 4 }}>{sub}</div>}
          </div>
          <button className="icon-btn" onClick={onClose} aria-label="Close"><Icon name="x" /></button>
        </div>
        <div className="modal-body">{children}</div>
        {footer && <div className="modal-foot">{footer}</div>}
      </div>
    </div>
  );
}

export function Field({ label, children, hint }: { label: string; children: ReactNode; hint?: ReactNode }) {
  return <label className="field"><span className="lbl">{label}</span>{children}{hint && <span className="muted" style={{ fontSize: 12 }}>{hint}</span>}</label>;
}

export function MoneyInput({ value, onChange, disabled }: { value: number | ''; onChange: (v: number | '') => void; disabled?: boolean }) {
  return (
    <div style={{ position: 'relative' }}>
      <span style={{ position: 'absolute', left: 12, top: '50%', transform: 'translateY(-50%)', color: '#7a8888' }}>₹</span>
      <input className="input num" inputMode="numeric" style={{ paddingLeft: 26 }} disabled={disabled} value={value}
        onChange={e => { const d = e.target.value.replace(/[^\d]/g, ''); onChange(d === '' ? '' : Math.min(Number(d), 99_99_99_999)); }} />
    </div>
  );
}

export function Segment<T extends string>({ value, options, onChange }: { value: T; options: { v: T; label: ReactNode; count?: number }[]; onChange: (v: T) => void }) {
  return (
    <div className="segment" role="tablist">
      {options.map(o => (
        <button key={o.v} role="tab" aria-selected={o.v === value} className={o.v === value ? 'on' : ''} onClick={() => onChange(o.v)}>
          {o.label}{o.count !== undefined && <span className="c num">{o.count}</span>}
        </button>
      ))}
    </div>
  );
}

export function Chips<T extends string>({ value, options, onChange }: { value: T; options: { v: T; label: ReactNode }[]; onChange: (v: T) => void }) {
  return <div className="chips">{options.map(o => <button key={o.v} type="button" className={`chip ${o.v === value ? 'on' : ''}`} onClick={() => onChange(o.v)}>{o.label}</button>)}</div>;
}

export function Toast() {
  const S = useStore();
  if (!S.toastMsg) return null;
  return <div className="toast no-print" role="status"><Icon name="check" size={16} />{S.toastMsg}</div>;
}

export function Empty({ title = 'Nothing here', children }: { title?: string; children?: ReactNode }) {
  return <div className="empty"><b>{title}</b>{children ?? 'No records match this filter. Try “All”, or clear the search.'}</div>;
}

/** Offers to install the app on this phone (home-screen icon, opens full screen). */
export function InstallBar() {
  const kind = useSyncExternalStore(onInstallChange, () => (dismissed() ? null : installKind()));
  const [steps, setSteps] = useState(false);
  if (!kind) return null;
  return (
    <div className="sync-bar install no-print">
      <Icon name="download" size={14} />
      {steps ? <span>In Safari, tap the <b>Share</b> button (square with an arrow), then <b>Add to Home Screen</b>.</span> : <span>Install Nandanam on this phone. It opens from your home screen like an app.</span>}
      {kind === 'prompt' && <button onClick={() => void install()}>Install app</button>}
      {kind === 'ios' && !steps && <button onClick={() => setSteps(true)}>Install app</button>}
      <button aria-label="Not now" onClick={dismiss}>Not now</button>
    </div>
  );
}

/** Saving / saved / offline / error / view-only / conflicts. Never hides a pending change. */
export function SyncBar() {
  const S = useStore(), e = S.engine, v = S.view;
  const issues = v.rejected.length + v.edits.length + v.reviews.length + v.clashes.length;
  const bars: ReactNode[] = [];
  bars.push(<InstallBar key="i" />);
  if (S.mode === 'demo') bars.push(<div key="d" className="sync-bar demo no-print"><Icon name="info" size={14} /><span className="sb-text">Demo mode<span className="sb-long"> — sample data stored only in this browser. Nothing is saved to OneDrive.</span><span className="sb-short"> · sample data, not saved to OneDrive</span></span><button onClick={() => S.nav('account')}>Account & sync</button></div>);
  const moved = S.onedrive?.movedTo;
  if (moved) bars.push(<div key="m" className="sync-bar denied no-print"><Icon name="lock" size={14} />This business moved to {moved.to}'s OneDrive. This old folder is read-only now.<button onClick={() => S.nav('account')}>What to do</button></div>);
  if (S.signInNeeded) bars.push(<div key="s" className="sync-bar error no-print"><Icon name="user" size={14} />Microsoft sign-in has expired. Changes cannot be saved until you sign in again.<button onClick={() => void signIn()}>Sign in</button></div>);
  else if (e.status === 'offline') bars.push(<div key="o" className="sync-bar offline no-print"><Icon name="cloudOff" size={14} />Offline — showing the copy from {e.lastSyncAt ? e.lastSyncAt.replace('T', ' ') : 'this device'}. New enquiries save as drafts; other changes need a connection.<button onClick={() => S.refresh()}>Retry</button></div>);
  else if (e.status === 'error') bars.push(<div key="e" className="sync-bar error no-print"><Icon name="alert" size={14} />Sync error — {e.lastError}<button onClick={() => S.refresh()}>Retry</button></div>);
  else if (e.status === 'denied') bars.push(<div key="x" className="sync-bar denied no-print"><Icon name="lock" size={14} />This account can no longer open the business folder. Showing the last copy, read-only.<button onClick={() => S.nav('account')}>Details</button></div>);
  if (e.access === 'viewer' && !moved) bars.push(<div key="v" className="sync-bar viewer no-print"><Icon name="eye" size={14} />View only — the owner has shared this folder with you as a viewer.</div>);
  if (e.pending.length && e.status !== 'saving') bars.push(<div key="p" className="sync-bar pending no-print"><Icon name="cloud" size={14} />{e.pending.length} change{e.pending.length > 1 ? 's' : ''} on this device not yet in OneDrive<button onClick={() => S.nav('account')}>Details</button></div>);
  if (S.updateReady) bars.push(<div key="u" className="sync-bar pending no-print"><Icon name="refresh" size={14} />A new version of the app is ready. It loads when you finish what you are doing.<button onClick={() => location.reload()}>Update now</button></div>);
  for (const p of S.problems) bars.push(<div key={'np' + p.opId} className="sync-bar error no-print"><Icon name="alert" size={14} />{p.what} was not saved — {p.error}<button onClick={() => S.dismissProblem(p.opId)}>Dismiss</button></div>);
  if (issues) bars.push(<div key="c" className="sync-bar conflict no-print"><Icon name="alert" size={14} />{issues} change{issues > 1 ? 's' : ''} from different devices need{issues > 1 ? '' : 's'} your review<button onClick={() => S.nav('account', { tab: 'review' })}>Review</button></div>);
  return <>{bars}</>;
}

export function SaveChip() {
  const S = useStore(), st = S.engine.status;
  if (S.uploading || st === 'saving') return <span className="pill st-neutral no-print" title="Saved on this device. Uploading to OneDrive and checking for changes from other devices."><Icon name="refresh" size={12} />Saved · syncing to OneDrive…</span>;
  if (S.savedFlash) return <span className="pill st-good no-print"><Icon name="check" size={12} />{S.mode === 'demo' ? 'Saved' : 'Saved to OneDrive'}</span>;
  return null;
}
